"use client";

import React from "react";

import { useState, useEffect, useRef } from "react";
import { CheckCircle, XCircle } from "lucide-react";
import axios from "axios";
import { base_url } from "../../config";
import AdminSpinner from "../../components/Spinner/Spinner";

interface StudentProofModalProps {
  isOpen: boolean;
  onClose: () => void;
  pdf: Blob;
  row: any;
  onAccept: () => void;
  onDecline: (reason: string) => void;
}

const DECLINE_REASONS = [
  "Your student document is not written in English",
  "Your student proof indicates that your studies will be completed before the conference dates",
  "Your student proof is compatible with finishing your studies before the conference dates",
  "Your student proof must contain a signature from your advisor or department head, or a stamp from your university, or an online verification link",
  "Your student document is not readable",
  "Your student document does not confirm that you are currently a student",
  "Other",
];

export default function StudentProofModal({
  isOpen,
  row,
  onClose,
  pdf,
  onAccept,
  onDecline,
}: StudentProofModalProps) {
  const [selectedReason, setSelectedReason] = useState<string>("");
  const [textReason, setTextReason] = useState<string>("");
  const [isDeclineActive, setIsDeclineActive] = useState<boolean>(false);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null); // State to store the Blob URL
  const modalRef = useRef<HTMLDivElement>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // Generate a Blob URL when the PDF Blob changes
  useEffect(() => {
    if (pdf) {
      const url = URL.createObjectURL(pdf);
      setPdfUrl(url);

      // Clean up the Blob URL when the component unmounts or the PDF changes
      return () => {
        URL.revokeObjectURL(url);
        setIsLoading(false);
      };
    }
  }, [pdf]);

  // Handle clicking outside the modal to close it
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        modalRef.current &&
        !modalRef.current.contains(event.target as Node)
      ) {
        handleClose();
      }
    }

    function handleEscapeKey(event: KeyboardEvent) {
      if (event.key === "Escape") {
        handleClose();
      }
    }

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("keydown", handleEscapeKey);
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscapeKey);
      document.body.style.overflow = "auto";
    };
  }, [isOpen]);

  const handleAccept = async () => {
    try {
      ;
      setIsLoading(true);
      await axios.post(`${base_url}/api/admin/accept_student_proof`, {
        student_id: row.student_id,
        code: row.code,
      });

      onAccept();
      onClose();
    } catch (error) {
      console.error("Error declining student proof:", error);
    } finally {
      setIsLoading(false);
    }

  };

  const handleDecline = async () => {
    setIsDeclineActive(false);
    try {
      await axios.post(`${base_url}/api/admin/decline_student_proof`, {
        reason: textReason,
        student_id: row.student_id,
        code: row.code,
      });

      onDecline(textReason);
      onClose();
    } catch (error) {
      console.error("Error declining student proof:", error);
    }
  };

  const resetForm = () => {
    setSelectedReason("");
    setTextReason("");
    setIsDeclineActive(false);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleReasonSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    setSelectedReason(value);
    if (value !== "Other") {
      setTextReason(value);
    } else {
      setTextReason("");
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div
        ref={modalRef}
        className="bg-white rounded-lg shadow-lg w-full max-w-4xl h-[80vh] flex flex-col p-6 relative"
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
      >
        {/* Close button */}
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
          aria-label="Close"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
        {isLoading ? (
          <div>
          <AdminSpinner />
        </div>
        ) : (
          <>
          {/* Header */ }
          < div className="mb-4">
        <h2 id="modal-title" className="text-xl font-bold">
          Student Proof Validation
        </h2>
        <p className="text-gray-600">
          Please review the student proof document for{" "}
          <strong>{row.name?row.name:""}</strong> ({row.email}) and take action.
        </p>
      </div>

      {/* PDF Viewer */}
      <div className="flex-1 min-h-0 mb-4 border border-gray-300 rounded-md overflow-hidden">
        {pdfUrl ? (
          <iframe
            src={`${pdfUrl}#toolbar=0&navpanes=0`}
            className="w-full h-full"
            title="Student proof document"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-gray-500">
            No document available
          </div>
        )}
      </div>

      {/* Actions */}
      {isDeclineActive ? (
        <div className="space-y-4">
          <div className="space-y-4">
            <div className="space-y-2">
              <label
                htmlFor="decline-reason"
                className="block text-sm font-medium text-gray-700"
              >
                Reason for declining
              </label>
              <select
                id="decline-reason"
                value={selectedReason}
                onChange={handleReasonSelect}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="" disabled>
                  Select a reason
                </option>
                {DECLINE_REASONS.map((reason) => (
                  <option key={reason} value={reason}>
                    {reason}
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-2">
              <label
                htmlFor="text-reason"
                className="block text-sm font-medium text-gray-700"
              >
                {selectedReason === "Other"
                  ? "Custom reason"
                  : "Edit reason (if needed)"}
              </label>
              <textarea
                id="text-reason"
                placeholder={
                  selectedReason === "Other"
                    ? "Please specify the reason for declining"
                    : "Edit the reason if needed"
                }
                value={textReason}
                onChange={(e) => setTextReason(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[80px]"
              />
            </div>
          </div>

          <div className="flex space-x-2">
            <button
              onClick={handleDecline}
              disabled={!selectedReason || !textReason.trim()}
              className={`px-4 py-2 rounded-md text-white ${!selectedReason || !textReason.trim()
                  ? "bg-red-300 cursor-not-allowed"
                  : "bg-red-600 hover:bg-red-700"
                }`}
            >
              Confirm Decline
            </button>
            <button
              onClick={() => handleClose()}
              className="px-4 py-2 border border-gray-300 rounded-md bg-white hover:bg-gray-50"
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <div className="flex justify-between">
          <button
            onClick={() => setIsDeclineActive(true)}
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-md bg-white hover:bg-gray-50"
          >
            <XCircle className="h-4 w-4" />
            Decline
          </button>
          <button
            onClick={handleAccept}
            className="flex items-center gap-2 px-4 py-2 rounded-md bg-green-600 text-white hover:bg-green-700"
          >
            <CheckCircle className="h-4 w-4" />
            Accept
          </button>
        </div>
      )}
      </>
      )}
    </div>
    </div >
  );
}
